// Ticket hỗ trợ trên nền Góp ý (docs/superpowers/specs/2026-10-12-ticket-gop-y-design.md).
// Luật thuần, không I/O — dùng chung cho apps/api, apps/admin-api, packages/finance và giao diện.

export const TICKET_STATUSES = ['pending', 'in_progress', 'resolved'] as const
export type TicketStatus = (typeof TICKET_STATUSES)[number]

export const TICKET_STATUS_LABEL: Record<TicketStatus, string> = {
  pending: 'Chờ tiếp nhận',
  in_progress: 'Đang xử lý',
  resolved: 'Đã giải quyết',
}

export const TICKET_CATEGORIES = ['bug', 'feature', 'ui_ux', 'other'] as const

export const RESOLUTION_NOTE_MAX = 2000
/** Nhãn admin NPP trên ticket: "Admin <tên hiển thị NPP>" (Q13). */
export function adminRoleLabel(npp: { displayName: string }): string {
  return `Admin ${npp.displayName}`
}
/** Nhãn admin khi chưa biết NPP — giữ cho mã cũ / test. */
export const ADMIN_ROLE_LABEL = adminRoleLabel({ displayName: 'NPP' })

/** Người tiếp nhận / giải quyết — chụp lại lúc thao tác (Q9). */
export interface TicketActor {
  userId: string
  name: string
  phone: string | null
  role: string
  isAdmin: boolean
}

/** Cấp trên trực tiếp nhận ticket: chỉ khi cha là CTV hoặc Đại lý (Q3). */
export function uplineOf(shop: { parId: string | null; parType: string | null } | null | undefined): string | null {
  if (!shop?.parId) return null
  return shop.parType === 'agency' || shop.parType === 'ctv' ? shop.parId : null
}

/** "CTV <tên>" / "Đại lý <tên>" — tên đơn vị đã bắt đầu bằng tiền tố thì giữ nguyên (không "CTV CTV …"). */
export function unitRoleLabel(tenantType: string | null | undefined, unitName: string): string | null {
  const prefix = tenantType === 'agency' ? 'Đại lý' : tenantType === 'ctv' ? 'CTV' : null
  if (!prefix) return null
  const name = unitName.trim()
  return name.toLocaleLowerCase('vi').startsWith(`${prefix.toLocaleLowerCase('vi')} `) ? name : `${prefix} ${name}`
}

export function displayName(u: { name?: string | null; phone?: string | null; email?: string | null }): string {
  return u.name?.trim() || u.phone?.trim() || u.email?.trim() || 'Không rõ tên'
}

/** Q4: chỉ chủ / quản lý của CTV hoặc Đại lý. */
export function canHandleDownline(tenantType: string | null | undefined, memberRole: string | null | undefined): boolean {
  return (tenantType === 'agency' || tenantType === 'ctv') && (memberRole === 'owner' || memberRole === 'manager')
}

export function normalizeResolutionNote(note: string): { ok: true; note: string } | { ok: false; message: string } {
  const v = note.trim()
  if (!v) return { ok: false, message: 'Nhập ghi chú kết quả' }
  if (v.length > RESOLUTION_NOTE_MAX) return { ok: false, message: `Ghi chú tối đa ${RESOLUTION_NOTE_MAX} ký tự` }
  return { ok: true, note: v }
}

export type ResolveDecision =
  | { ok: true; alsoTake: boolean }
  | { ok: false; kind: 'conflict' | 'forbidden'; code: string; message: string }

/**
 * Q5 + Q7. `alsoTake` = chưa có người tiếp nhận (pending, hoặc in_progress cũ từ "reviewed")
 * → người giải quyết được ghi luôn là người tiếp nhận.
 */
export function decideResolve(
  t: { status: string; handledByUserId: string | null },
  actor: { userId: string; isAdmin: boolean },
): ResolveDecision {
  if (t.status === 'resolved') return { ok: false, kind: 'conflict', code: 'ticket.resolved', message: 'Ticket đã được giải quyết' }
  if (!t.handledByUserId) return { ok: true, alsoTake: true }
  if (t.handledByUserId === actor.userId || actor.isAdmin) return { ok: true, alsoTake: false }
  return { ok: false, kind: 'forbidden', code: 'ticket.not_handler', message: 'Chỉ người đã tiếp nhận hoặc admin được giải quyết ticket này' }
}

/** Góp ý từ trang chủ không có tài khoản → không có ai để báo. */
export function senderNoticeTarget(t: { userId: string | null }): string | null {
  return t.userId ?? null
}

export function ticketTakenNotice(i: { ticketTitle: string; name: string; role: string; phone: string | null }) {
  const who = i.phone ? `${i.role}, ${i.phone}` : i.role
  return { title: 'Góp ý của bạn đã được tiếp nhận', message: `${i.name} (${who}) đang xử lý: ${i.ticketTitle}` }
}

export function ticketResolvedNotice(i: { name: string; role: string; note: string }) {
  return { title: 'Góp ý của bạn đã được giải quyết', message: `${i.name} (${i.role}): ${i.note}` }
}

export function newTicketNotice(i: { shopName: string; ticketTitle: string }) {
  return { title: `Góp ý mới từ ${i.shopName}`, message: i.ticketTitle }
}

export function ticketUrl(tab: 'history' | 'downline', id: string): string {
  return `/feedback?tab=${tab}&id=${id}`
}

export const TICKET_PAGE_SIZE = 50

/** Phân trang danh sách ticket (trang tính từ 0). `from`/`to` để hiện "101–120 / 120". */
export function pageRange(page: number, size: number, total: number) {
  const p = Math.max(0, Math.floor(page))
  const offset = p * size
  return {
    offset,
    from: total === 0 ? 0 : Math.min(offset + 1, total),
    to: Math.min(offset + size, total),
    hasPrev: p > 0,
    hasNext: offset + size < total,
  }
}

/**
 * Tab trang Góp ý từ URL. Thông báo "Ticket mới" có thể thuộc đơn vị khác đơn vị đang chọn
 * (chuông hiện thông báo mọi đơn vị) → `downlineBlocked` để trang nhắc chuyển đơn vị.
 */
export function resolveFeedbackTab(urlTab: string | null, canDownline: boolean): { tab: 'form' | 'history' | 'downline'; downlineBlocked: boolean } {
  if (urlTab === 'history') return { tab: 'history', downlineBlocked: false }
  if (urlTab === 'downline') return canDownline ? { tab: 'downline', downlineBlocked: false } : { tab: 'form', downlineBlocked: true }
  return { tab: 'form', downlineBlocked: false }
}

/**
 * Ticket gửi cho cấp trên: bỏ IP / trình duyệt (metadata) và mã tài khoản người gửi; `canResolve`
 * tính ở server theo người đang xem (Q7) — giao diện không phải tự so người tiếp nhận.
 */
export function toDownlineTicket<T extends { userId?: unknown; metadata?: unknown; status: string; handledByUserId: string | null }>(
  t: T,
  viewer: { userId: string; isAdmin: boolean },
): Omit<T, 'userId' | 'metadata'> & { canResolve: boolean } {
  const { userId: _u, metadata: _m, ...rest } = t
  return { ...rest, canResolve: decideResolve(t, viewer).ok }
}
